import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import {
  Btn,
  CustomCheckbox,
  CustomDropdown,
  CustomInputTextArea,
  CustomModal,
  InputNumeroDecimal,
  InputText,
} from '../../../../components'
import {
  LIMITE_DESCRICAO_NFSE,
  MARCADORES_DESCRICAO_NFSE,
  RETENCAO_ISS_NAO_RETIDO,
  TIPOS_RETENCAO_ISS,
  TRIBUTACAO_ISS_TRIBUTAVEL,
  TRIBUTACOES_ISS,
} from '../../../../constants/fiscal'
import * as ServicoNfseHttp from '../../../../services/api/https/registration/servico-nfse.http'
import { errorHandler } from '../../../../services/util/error.service'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import { erroAliquota, isTransporteMunicipal, toNum } from '../../Fiscal/EmitirNfse/emitirNfse.util'
import SelectCodigoNfse from './SelectCodigoNfse'

const estiloDica = { color: 'gray', fontSize: 12 }

const buildForm = () => ({
  id: undefined,
  descricao: '',
  tributacaoNacional: null,
  nbs: null,
  codigo_tributacao_municipal: '',
  aliquota_iss: '',
  tributacao_iss: TRIBUTACAO_ISS_TRIBUTAVEL,
  tipo_retencao_iss: RETENCAO_ISS_NAO_RETIDO,
  modelo_descricao: '',
  padrao_frete_municipal: false,
  actived: true,
})

async function itemDaLista(buscar, codigo) {
  if (!codigo) return null
  const itens = await buscar(codigo)
  return (itens || []).find(item => item.codigo === codigo) || null
}

function erroDoFormulario(form) {
  if (!form.descricao.trim()) return 'Informe a descrição do serviço'
  if (!form.tributacaoNacional) return 'Selecione o Código de Tributação Nacional na lista'
  if (!form.nbs) return 'Selecione o código NBS na lista'
  const municipal = String(form.codigo_tributacao_municipal || '').trim()
  if (municipal && !/^\d{3}$/.test(municipal))
    return 'O Código de Tributação Municipal deve ter 3 dígitos'
  const aliquota = toNum(form.aliquota_iss)
  if (form.tributacao_iss === TRIBUTACAO_ISS_TRIBUTAVEL && aliquota !== null)
    return erroAliquota(aliquota, form.tributacaoNacional.codigo)
  if (String(form.modelo_descricao || '').length > LIMITE_DESCRICAO_NFSE)
    return `O modelo da descrição deve ter no máximo ${LIMITE_DESCRICAO_NFSE} caracteres`
  return null
}

function ServicoNfseModal({ show, data, onModalClose, onModalSave, selectedTp, dispatch }) {
  const [form, setForm] = useState(buildForm)

  const atualizar = patch => setForm(atual => ({ ...atual, ...patch }))
  const onError = error => dispatch(showMessageBox(null, errorHandler(error)))

  useEffect(() => {
    if (!show) return undefined
    let ativo = true
    if (!data) {
      setForm(buildForm())
      return undefined
    }
    setForm({
      ...buildForm(),
      ...data,
      codigo_tributacao_municipal: data.codigo_tributacao_municipal || '',
      aliquota_iss:
        data.aliquota_iss !== null && data.aliquota_iss !== undefined
          ? String(data.aliquota_iss)
          : '',
      modelo_descricao: data.modelo_descricao || '',
    })
    Promise.all([
      itemDaLista(
        ServicoNfseHttp.getCodigosTributacaoNacionalNfse,
        data.codigo_tributacao_nacional,
      ),
      itemDaLista(ServicoNfseHttp.getCodigosNbsNfse, data.codigo_nbs),
    ])
      .then(([tributacaoNacional, nbs]) => ativo && atualizar({ tributacaoNacional, nbs }))
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const transporte = Boolean(
    form.tributacaoNacional && isTransporteMunicipal(form.tributacaoNacional.codigo),
  )
  const tributavel = form.tributacao_iss === TRIBUTACAO_ISS_TRIBUTAVEL

  // Events
  const onBtnSalvarClicked = async () => {
    const erro = erroDoFormulario(form)
    if (erro) return onError(erro)
    try {
      dispatch(showLoadingBox('Aguarde...', 'Salvando serviço'))
      const salvo = await ServicoNfseHttp.saveServicoNfse({
        id: form.id,
        tp_id: selectedTp,
        descricao: form.descricao.trim(),
        codigo_tributacao_nacional: form.tributacaoNacional.codigo,
        codigo_nbs: form.nbs.codigo,
        codigo_tributacao_municipal: String(form.codigo_tributacao_municipal || '').trim() || null,
        aliquota_iss: tributavel ? toNum(form.aliquota_iss) : null,
        tributacao_iss: form.tributacao_iss,
        tipo_retencao_iss: form.tipo_retencao_iss,
        modelo_descricao: String(form.modelo_descricao || '').trim() || null,
        padrao_frete_municipal: transporte && form.actived && form.padrao_frete_municipal,
        actived: form.actived,
      })
      dispatch(hideLoadingBox())
      dispatch(showMessageBox('Serviço salvo com sucesso', null))
      onModalSave(salvo)
    } catch (error) {
      dispatch(hideLoadingBox())
      onError(error)
    }
  }

  // Render
  return (
    <CustomModal header="Serviço (NFS-e)" isVisible={show} onClose={onModalClose} width={800}>
      <form className="p-grid">
        <div className="p-col-12">
          <InputText
            label="Descrição:*"
            value={form.descricao}
            maxLength={255}
            onChange={descricao => atualizar({ descricao })}
          />
        </div>
        <div className="p-col-12">
          <SelectCodigoNfse
            label="Código de Tributação Nacional:*"
            value={form.tributacaoNacional}
            buscar={ServicoNfseHttp.getCodigosTributacaoNacionalNfse}
            placeholder="Digite o código ou parte da descrição"
            dica="Lista nacional de serviços (LC 116). Frete municipal: 16.02.01"
            onChange={tributacaoNacional =>
              atualizar({
                tributacaoNacional,
                ...(tributacaoNacional && isTransporteMunicipal(tributacaoNacional.codigo)
                  ? {}
                  : { padrao_frete_municipal: false }),
              })
            }
            onError={onError}
          />
        </div>
        <div className="p-md-8 p-col-12">
          <SelectCodigoNfse
            label="NBS:*"
            value={form.nbs}
            buscar={ServicoNfseHttp.getCodigosNbsNfse}
            placeholder="Digite o código ou parte da descrição"
            dica="Nomenclatura Brasileira de Serviços (correlação da NFS-e nacional)"
            onChange={nbs => atualizar({ nbs })}
            onError={onError}
          />
        </div>
        <div className="p-md-4 p-col-12">
          <InputText
            label="Código de Tributação Municipal:"
            marginTop={9}
            value={form.codigo_tributacao_municipal}
            maxLength={3}
            onChange={valor => atualizar({ codigo_tributacao_municipal: valor.replace(/\D/g, '') })}
          />
          <span style={estiloDica}>Só quando a prefeitura exigir (3 dígitos)</span>
        </div>
        <div className="p-md-4 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={TRIBUTACOES_ISS}
            value={TRIBUTACOES_ISS.find(item => item.value === form.tributacao_iss) || null}
            label="Tributação do ISS:*"
            onChange={item =>
              atualizar({
                tributacao_iss: item.value,
                ...(item.value === TRIBUTACAO_ISS_TRIBUTAVEL
                  ? {}
                  : { tipo_retencao_iss: RETENCAO_ISS_NAO_RETIDO, aliquota_iss: '' }),
              })
            }
          />
        </div>
        <div className="p-md-4 p-col-12">
          <CustomDropdown
            optionLabel="label"
            options={TIPOS_RETENCAO_ISS}
            value={TIPOS_RETENCAO_ISS.find(item => item.value === form.tipo_retencao_iss) || null}
            label="Retenção do ISS:*"
            disabled={!tributavel}
            onChange={item => atualizar({ tipo_retencao_iss: item.value })}
          />
        </div>
        <div className="p-md-4 p-col-12">
          {tributavel && (
            <>
              <InputNumeroDecimal
                label="Alíquota do ISS (%):"
                marginTop={10}
                value={form.aliquota_iss}
                onChange={aliquota_iss => atualizar({ aliquota_iss })}
              />
              <span style={estiloDica}>Vem como padrão na emissão e pode ser alterada lá</span>
            </>
          )}
        </div>
        <div className="p-col-12">
          <CustomInputTextArea
            label="Modelo da descrição"
            marginTop={10}
            rows={3}
            value={form.modelo_descricao}
            onChange={modelo_descricao => atualizar({ modelo_descricao })}
          />
          <span style={estiloDica}>
            {String(form.modelo_descricao || '').length}/{LIMITE_DESCRICAO_NFSE} · Marcadores:{' '}
            {MARCADORES_DESCRICAO_NFSE.map(item => `${item.marcador} (${item.descricao})`).join(
              ', ',
            )}
          </span>
        </div>
        <div className="p-col-12">
          <CustomCheckbox
            checked={form.padrao_frete_municipal && transporte && form.actived}
            disabled={!transporte || !form.actived}
            onChange={padrao_frete_municipal => atualizar({ padrao_frete_municipal })}
          >
            Padrão do frete municipal (já vem selecionado na emissão pela rota)
          </CustomCheckbox>
        </div>
        <div className="p-col-12">
          <CustomCheckbox checked={form.actived} onChange={actived => atualizar({ actived })}>
            Ativo
          </CustomCheckbox>
        </div>
        <div className="p-col-12">
          <Btn onClick={onBtnSalvarClicked}>Salvar</Btn>
        </div>
      </form>
    </CustomModal>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(ServicoNfseModal)
